import React from "react";
import { 
  BookOpen, 
  Clock, 
  Star, 
  Users, 
  IndianRupee, 
  PlayCircle, 
  Globe, 
  Zap,
  Info,
  ExternalLink,
  ShieldCheck,
  Video
} from "lucide-react";
import Button from "../../../components/Button";
import Dialog from "../../../components/Dialog";

const ViewCourse = ({ open, onClose, course }) => {
  if (!course) return null;

  const footer = (
    <Button variant="secondary" size="sm" onClick={onClose} className="px-8">
      Close
    </Button>
  );

  const StatItem = ({ icon: Icon, label, value, colorClass }) => (
    <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-start gap-4 transition-all hover:shadow-md hover:border-primary-100 group">
      <div className={`p-2.5 rounded-xl ${colorClass} transition-transform group-hover:scale-110`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">
          {label}
        </p>
        <p className="text-sm font-bold text-gray-800">
          {value || "Not Specified"}
        </p>
      </div>
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 text-primary-600 rounded-lg">
            <BookOpen className="w-5 h-5" />
          </div>
          <span>Course Details</span>
        </div>
      }
      size="xl"
      footer={footer}
    >
      <div className="space-y-8 py-2">
        {/* Hero Section with Image & Title */}
        <div className="relative overflow-hidden rounded-3xl bg-gray-900 shadow-xl group">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0">
            {course.imageUrl ? (
              <img 
                src={course.imageUrl} 
                alt={course.title} 
                className="w-full h-full object-cover opacity-30 blur-[2px] transition-transform duration-500 group-hover:scale-105"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent" />
          </div>
          
          <div className="relative z-10 p-8 md:p-10 flex flex-col md:flex-row gap-8 items-center md:items-end">
            {/* Main Thumbnail */}
            <div className="w-48 h-48 md:w-56 md:h-56 shrink-0 rounded-2xl overflow-hidden shadow-2xl border-2 border-white/10 group-hover:border-primary-400 transition-colors">
              {course.imageUrl ? (
                <img src={course.imageUrl} alt={course.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                  <BookOpen className="w-12 h-12 text-gray-600" />
                </div>
              )}
            </div>

            <div className="flex-1 text-center md:text-left">
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mb-4">
                <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                  course.courseType === 'crypto' 
                    ? "bg-orange-500/20 text-orange-400 border-orange-500/30" 
                    : "bg-blue-500/20 text-blue-400 border-blue-500/30"
                }`}>
                  {course.courseType === 'crypto' ? 'Crypto' : 'Indian Market'}
                </span>
                {course.underGrowthPlan && (
                  <span className="bg-primary-500/20 text-primary-400 border border-primary-500/30 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">
                    <Zap className="w-3 h-3 inline mr-1" /> Growth Plan
                  </span>
                )}
              </div>
              
              <h1 className="text-3xl md:text-5xl font-black text-white mb-4 tracking-tight leading-tight">
                {course.title}
              </h1>
              
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 text-gray-300 font-bold text-sm">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span>{course.rating || '0.0'} Rating</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary-400" />
                  <span>{course.studentCount || '0'} Students</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-secondary-400" />
                  <span>{course.duration || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing & Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatItem 
            icon={IndianRupee} 
            label="Special Price" 
            value={`₹${course.price || 0}`} 
            colorClass="bg-emerald-50 text-emerald-600"
          />
          <StatItem 
            icon={Zap} 
            label="Original Price" 
            value={course.originalPrice ? <span className="line-through opacity-50">₹{course.originalPrice}</span> : "N/A"} 
            colorClass="bg-purple-50 text-purple-600"
          />
          <StatItem 
            icon={Globe} 
            label="Access Level" 
            value={course.underGrowthPlan ? "Subscription" : "One-time Buy"} 
            colorClass="bg-blue-50 text-blue-600"
          />
          <StatItem 
            icon={ShieldCheck} 
            label="Certification" 
            value="Certificate Included" 
            colorClass="bg-orange-50 text-orange-600"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Description & Video */}
          <div className="lg:col-span-2 space-y-8">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600">
                  <Info className="w-4 h-4" />
                </div>
                <h3 className="text-lg font-black text-gray-800">About This Course</h3>
              </div>
              <div className="bg-gray-50/50 rounded-3xl p-8 border border-gray-100 text-gray-700 leading-relaxed text-sm italic shadow-inner">
                {course.description || "The curriculum for this course is being refined by our experts. Reach out to support for full details."}
              </div>
            </div>

            {course.basicVideoUrl && (
              <div className="bg-primary-900 rounded-3xl p-8 text-white relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <PlayCircle className="w-32 h-32" />
                </div>
                <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
                  <div>
                    <h4 className="text-2xl font-black mb-2">Intro Video Available</h4>
                    <p className="text-white/70 text-sm font-medium">Watch the course teaser to understand the value proposition.</p>
                  </div>
                  <a 
                    href={course.basicVideoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-white text-primary-900 px-6 py-3 rounded-2xl font-black hover:bg-primary-50 transition-colors shadow-xl"
                  >
                    <Video className="w-5 h-5" />
                    Watch Teaser
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Right Column: Badges & Features */}
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600">
                  <Zap className="w-4 h-4" />
                </div>
                <h3 className="text-lg font-black text-gray-800">Course Highlights</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {Array.isArray(course.badges) && course.badges.length > 0 ? (
                  course.badges.map((tag, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-white border border-gray-100 shadow-sm text-gray-700 rounded-2xl text-xs font-bold flex items-center gap-2 transition-all hover:border-primary-300 hover:text-primary-700"
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-primary-400"></div>
                      {tag}
                    </span>
                  ))
                ) : (
                  <div className="text-sm text-gray-400 font-medium bg-gray-50 border border-dashed border-gray-200 rounded-2xl p-6 w-full text-center">
                    No badges active
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-3xl text-white shadow-lg">
                <h4 className="font-black mb-2">Lifetime Access</h4>
                <p className="text-xs text-white/80 font-medium leading-relaxed">
                  Enroll once and access the course materials forever. Includes all future curriculum updates for free.
                </p>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ViewCourse;
